import { Routes, Route, Navigate } from 'react-router';
import { useAuth } from './hooks/useAuth';
import { DashboardLayout } from './components/DashboardLayout';
import { LoginPage } from './routes/Login';
import { DashboardPage } from './routes/Dashboard';
import { WorkspacesPage } from './routes/Workspaces';
import { WorkspaceSettingsPage } from './routes/WorkspaceSettings';
import { WorkspaceDashboardPage } from './routes/WorkspaceDashboard';
import { WorkspaceCreatePage } from './routes/WorkspaceCreate';
import { BusinessContextPage } from './routes/BusinessContext';
import { TeamManagementPage } from './routes/TeamManagement';
import { AcceptInvitePage } from './routes/AcceptInvite';
import { ProfilePage } from './routes/Profile';
import { AccountsPage } from './routes/Accounts';
import { AccountDetailPage } from './routes/AccountDetail';
import { IntelligenceReportPage } from './routes/IntelligenceReport';
import { PillarDetailPage } from './routes/PillarDetail';
import ArticleQueue from './routes/ArticleQueue';
import ArticleDetail from './routes/ArticleDetail';
import { BillingPage } from './routes/Billing';
import { AccountSettingsPage } from './routes/AccountSettings';
import { ForgotPasswordPage } from './routes/ForgotPassword';
import { ResetPasswordPage } from './routes/ResetPassword';
import { MagicLinkVerifyPage } from './routes/MagicLinkVerify';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center">
        <div className="text-helm-400 text-lg">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Super admins should never see another account's workspace in their own
 * context. If they want to view workspace data they must impersonate an
 * admin on that account first. Redirect them to /accounts.
 */
function SuperAdminWorkspaceGuard({ children }: { children: React.ReactNode }) {
  const { user, isImpersonating } = useAuth();
  if (user?.role === 'super_admin' && !isImpersonating) {
    return <Navigate to="/accounts" replace />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center">
        <div className="text-helm-400 text-lg">Loading...</div>
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/magic-link/verify" element={<MagicLinkVerifyPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Routes>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/workspaces" element={<WorkspacesPage />} />
                <Route path="/workspaces/new" element={<WorkspaceCreatePage />} />
                <Route path="/workspaces/:workspaceId" element={<SuperAdminWorkspaceGuard><WorkspaceDashboardPage /></SuperAdminWorkspaceGuard>} />
                <Route path="/workspaces/:workspaceId/business-context" element={<SuperAdminWorkspaceGuard><BusinessContextPage /></SuperAdminWorkspaceGuard>} />
                <Route path="/workspaces/:workspaceId/intelligence" element={<SuperAdminWorkspaceGuard><IntelligenceReportPage /></SuperAdminWorkspaceGuard>} />
                <Route path="/workspaces/:workspaceId/pillars/:pillarId" element={<SuperAdminWorkspaceGuard><PillarDetailPage /></SuperAdminWorkspaceGuard>} />
                <Route path="/workspaces/:workspaceId/articles" element={<SuperAdminWorkspaceGuard><ArticleQueue /></SuperAdminWorkspaceGuard>} />
                <Route path="/workspaces/:workspaceId/articles/:articleId" element={<SuperAdminWorkspaceGuard><ArticleDetail /></SuperAdminWorkspaceGuard>} />
                <Route path="/workspaces/:workspaceId/settings" element={<SuperAdminWorkspaceGuard><WorkspaceSettingsPage /></SuperAdminWorkspaceGuard>} />
                <Route path="/team" element={<TeamManagementPage />} />
                <Route path="/accounts" element={<AccountsPage />} />
                <Route path="/accounts/:accountId" element={<AccountDetailPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/settings" element={<AccountSettingsPage />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
